import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, type Task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import PowerSelect from 'ember-power-select/components/power-select';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import { or } from 'ember-truth-helpers';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import UiButton from 'garaje/components/ui-button';
import type Location from 'garaje/models/location';

interface ReminderOption {
  label: string;
  value: number;
  disabled?: boolean;
}

export const DEFAULT_THRESHOLD_MINUTES = 1440;

const REMINDER_OPTIONS: ReminderOption[] = [
  { label: '72 hours', value: 4320 },
  { label: '48 hours', value: 2880 },
  { label: '24 hours (Default)', value: DEFAULT_THRESHOLD_MINUTES },
];

interface VisitorReminderSettingsSignature {
  Args: {
    location: Location;
    saveTask: Task<void, [Location]>;
  };
}

export default class VisitorReminderSettings extends Component<VisitorReminderSettingsSignature> {
  @tracked isOpen = false;
  @tracked pendingOption: ReminderOption | null = null;

  get isNearVisitEnabled(): boolean {
    return this.args.location.nearVisitScreeningEnabled;
  }

  get isMisconfigured(): boolean {
    return this.pendingOption?.value !== DEFAULT_THRESHOLD_MINUTES && this.isNearVisitEnabled;
  }

  get options(): ReminderOption[] {
    if (!this.isNearVisitEnabled) return REMINDER_OPTIONS;
    return REMINDER_OPTIONS.map((o) => (o.value === DEFAULT_THRESHOLD_MINUTES ? o : { ...o, disabled: true }));
  }

  get defaultOption(): ReminderOption {
    return this.options.find((o) => o.value === DEFAULT_THRESHOLD_MINUTES)!;
  }

  get currentOption(): ReminderOption {
    const current = this.args.location.visitorReminderThresholdMinutes ?? DEFAULT_THRESHOLD_MINUTES;
    return this.options.find((o) => o.value === current) ?? this.defaultOption;
  }

  get displayOption(): ReminderOption {
    return this.pendingOption ?? this.currentOption;
  }

  get noChangesMade(): boolean {
    return this.displayOption.value === this.currentOption.value;
  }

  edit = (): void => {
    this.pendingOption = this.currentOption;
    this.isOpen = true;
  };

  cancel = (): void => {
    this.pendingOption = null;
    this.isOpen = false;
  };

  setOption = (option: ReminderOption): void => {
    this.pendingOption = option;
  };

  save = dropTask(async () => {
    if (this.noChangesMade || this.isMisconfigured) return;

    const { location, saveTask } = this.args;
    const option = this.pendingOption ?? this.currentOption;

    location.visitorReminderThresholdMinutes = option.value;
    await saveTask.perform(location);

    // Parent task rolls attributes back on failure, so a mismatch means the save did not persist.
    if (location.visitorReminderThresholdMinutes !== option.value) return;

    this.pendingOption = null;
    this.isOpen = false;
  });

  <template>
    <SettingsPanel
      @isOpen={{this.isOpen}}
      @onCancel={{this.cancel}}
      data-test-visitor-reminder-settings
      ...attributes
      as |sp|
    >
      <sp.header
        @iconUri="/assets/images/features/email.svg"
        @title="Visitor reminder email"
        @description="Send visitors a reminder email before their scheduled visit"
        as |header|
      >
        <header.controls>
          {{#if sp.isOpen}}
            <UiButton
              data-test-save-visitor-reminder-settings
              class="btn btn--small btn--brand"
              disabled={{or this.save.isRunning this.noChangesMade this.isMisconfigured}}
              {{on "click" (perform this.save)}}
            >
              {{if this.save.isRunning "Saving..." "Save"}}
            </UiButton>
          {{else}}
            <UiButton
              data-test-edit-visitor-reminder-settings
              class="btn btn--small btn--brand"
              {{on "click" this.edit}}
            >
              Edit
            </UiButton>
          {{/if}}
        </header.controls>
      </sp.header>

      <sp.body as |Content|>
        <Content @style="inset">
          <div class="flex items-center gap-3 my-4" data-test-visitor-reminder-settings-body>
            <span>Send</span>
            <div class="w-48">
              <PowerSelect
                data-test-visitor-reminder-settings-dropdown
                @searchEnabled={{false}}
                @ariaLabel="Reminder threshold"
                @options={{this.options}}
                @selected={{this.displayOption}}
                @onChange={{this.setOption}}
                class="!m-0"
                as |option|
              >
                <span class="block pointer-events-auto">
                  {{option.label}}
                  {{#if option.disabled}}
                    <EmberTooltip
                      @side="top"
                      @text="Near visit registration is enabled."
                      @popperContainer="body"
                      @tooltipClass="tooltip !z-[1001]"
                    />
                  {{/if}}
                </span>
              </PowerSelect>
            </div>
            <span>before visit</span>
          </div>
          {{#if this.isNearVisitEnabled}}
            <MessageBox
              @type="warning"
              @header="Near visit registration is enabled"
              class="mt-4"
              data-test-visitor-reminder-settings-near-visit-warning
            >
              <p class="mb-0">Visitors won't be able to register until 24 hours before their visit</p>
            </MessageBox>
          {{/if}}
        </Content>
      </sp.body>
    </SettingsPanel>
  </template>
}
